import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, BarChart3, Loader2, PieChart as PieIcon, TrendingUp, TrendingDown, Lightbulb } from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell, LabelList,
} from 'recharts';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import useCentrosPermitidos from '@/hooks/useCentrosPermitidos';
import { compareMonthlySummaries } from '@/pages/dashboard/services/monthlySummaries.services';

const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16', '#f97316', '#14b8a6'];

const num0 = (v) => Number(v || 0).toLocaleString('es-CO', { maximumFractionDigits: 0 });
const dec2 = (v) => Number(v || 0).toLocaleString('es-CO', { maximumFractionDigits: 2 });
const cop = (v) => `$${num0(v)}`;
const cop2 = (v) => `$${dec2(v)}`;
const pct = (v) => `${Number(v || 0).toFixed(1)}%`;
// Formateador de etiquetas sobre barras: oculta ceros/nulos para no saturar.
const hideZero = (fmt) => (v) => (Number(v) ? fmt(v) : '');

const lastClosedPeriod = () => {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

// Gráfico de tendencia: eje X = períodos, una barra por centro.
const TrendBars = ({ data, centers, formatter, height = 300 }) => (
  <div style={{ width: '100%', height }}>
    <ResponsiveContainer>
      <BarChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis dataKey="period" tick={{ fontSize: 12 }} />
        <YAxis tick={{ fontSize: 12 }} />
        <Tooltip formatter={formatter} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        {centers.map((c, i) => (
          <Bar key={c.id} dataKey={c.nombre} fill={COLORS[i % COLORS.length]}>
            <LabelList dataKey={c.nombre} position="top" fontSize={9} fill="#64748b" formatter={hideZero(formatter)} />
          </Bar>
        ))}
      </BarChart>
    </ResponsiveContainer>
  </div>
);

// Panel de conclusiones por sección: mejor, menos eficiente y sugerencia.
const InsightPanel = ({ best, worst, bestText, worstText, suggestion }) => {
  if (!best || !worst) return null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
      <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3">
        <div className="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
          <TrendingUp className="h-3.5 w-3.5" /> MEJOR
        </div>
        <div className="text-sm font-semibold mt-1">{best.nombre}</div>
        <div className="text-xs text-muted-foreground mt-0.5">{bestText}</div>
      </div>
      <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3">
        <div className="text-[11px] font-semibold text-red-600 dark:text-red-400 flex items-center gap-1">
          <TrendingDown className="h-3.5 w-3.5" /> MENOS EFICIENTE
        </div>
        <div className="text-sm font-semibold mt-1">{worst.nombre}</div>
        <div className="text-xs text-muted-foreground mt-0.5">{worstText}</div>
      </div>
      <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
        <div className="text-[11px] font-semibold text-amber-600 dark:text-amber-400 flex items-center gap-1">
          <Lightbulb className="h-3.5 w-3.5" /> SUGERENCIA
        </div>
        <div className="text-xs text-muted-foreground mt-1">{suggestion}</div>
      </div>
    </div>
  );
};

const ComparativoLavanderiasPage = () => {
  const { toast } = useToast();
  const navigate = useNavigate();

  const now = new Date();
  const [from, setFrom] = useState(`${now.getFullYear()}-01`);
  const [to, setTo] = useState(lastClosedPeriod());

  const [allLaundries, setAllLaundries] = useState([]);
  const { tieneRestriccion, idsPermitidos, puedeVerCentro, cargarCentros } =
    useCentrosPermitidos();

  const [selectedIds, setSelectedIds] = useState([]);

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  const loadLaundries = async () => {
    try {
      const list = await cargarCentros();
      setAllLaundries(list);
      // Centros deseleccionados por defecto: el usuario elige cuáles comparar.
      // Con centros asignados son pocos, así que se preseleccionan todos.
      if (tieneRestriccion) setSelectedIds(list.map((l) => l._id));
    } catch (e) {
      toast({ title: 'Error', description: 'No se pudieron cargar los centros', variant: 'destructive' });
    }
  };

  // Carga los consolidados de TODOS los centros para el rango; la selección de
  // chips filtra en vivo qué se muestra (sin re-consultar).
  const loadData = async () => {
    setLoading(true);
    try {
      const resp = await compareMonthlySummaries({
        from,
        to,
        laundryIds: tieneRestriccion ? idsPermitidos : undefined,
      });
      setResult(resp?.data || null);
      setHasLoaded(true);
    } catch (e) {
      toast({ title: 'Error', description: e.message || 'No se pudo cargar el comparativo', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLaundries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tieneRestriccion]);

  // Recarga al entrar y cada vez que cambia el rango de meses.
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, tieneRestriccion]);

  const toggleId = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const toggleAll = () => {
    if (selectedIds.length === allLaundries.length) setSelectedIds([]);
    else setSelectedIds(allLaundries.map((l) => l._id));
  };

  const periods = result?.periods || [];
  // Se filtra tambien la respuesta: si alguien quita el `laundryIds` de arriba,
  // la UI sigue sin mostrar centros ajenos.
  const allCenters = (result?.laundries || []).filter((c) => puedeVerCentro(c.id));
  // Centros visibles = los que están seleccionados en los chips. Excluir un
  // centro lo quita de TODAS las secciones de inmediato (sin re-consultar).
  const centers = useMemo(
    () => allCenters.filter((c) => selectedIds.includes(c.id)),
    [allCenters, selectedIds],
  );

  const kpisMap = useMemo(() => {
    const map = new Map();
    (result?.summaries || [])
      .filter((s) => puedeVerCentro(s.laundryId))
      .forEach((s) => map.set(`${s.laundryId}|${s.periodLabel}`, s.kpis || {}));
    return map;
  }, [result]);

  const getVal = (id, period, key) => {
    const o = kpisMap.get(`${id}|${period}`);
    if (!o) return null;
    const v = Number(o[key]);
    return Number.isNaN(v) ? null : v;
  };

  // Series de tendencia (período × centro) para un KPI.
  const buildTrend = (key) =>
    periods.map((p) => {
      const row = { period: p };
      centers.forEach((c) => {
        const v = getVal(c.id, p, key);
        row[c.nombre] = v == null ? 0 : v;
      });
      return row;
    });

  // Agregados por centro sobre todo el rango (ratios = cociente de sumas).
  const aggregates = useMemo(() => {
    return centers.map((c) => {
      let kilos = 0, rechazo = 0, cP = 0, cS = 0, cMO = 0, litros = 0, horas = 0;
      let topPeriod = '', productoTop = null;
      periods.forEach((p) => {
        const o = kpisMap.get(`${c.id}|${p}`);
        if (!o) return;
        kilos += Number(o.kilosProcesados || 0);
        rechazo += Number(o.kilosRechazo || 0);
        cP += Number(o.costoProductos || 0);
        cS += Number(o.costoServicios || 0);
        cMO += Number(o.costoManoObra || 0);
        litros += Number(o.totalConsumoProductos || 0);
        horas += Number(o.horasAutorizadas || 0);
        if (o.productoTop && p >= topPeriod) { topPeriod = p; productoTop = o.productoTop; }
      });
      const base = kilos || 1;
      return {
        id: c.id,
        nombre: c.nombre,
        kilos,
        pctRechazo: (rechazo / base) * 100,
        kgPorHora: kilos / (horas || 1),
        costoProductosPorKg: cP / base,
        costoServiciosPorKg: cS / base,
        costoManoObraPorKg: cMO / base,
        costoKiloTotal: (cP + cS + cMO) / base,
        litrosPorKg: litros / base,
        Productos: cP / base,
        Servicios: cS / base,
        'Mano de obra': cMO / base,
        productoTop,
        topPeriod,
      };
    });
  }, [centers, periods, kpisMap]);

  const pieData = aggregates.filter((a) => a.kilos > 0).map((a) => ({ name: a.nombre, value: a.kilos }));
  const totalKilos = pieData.reduce((s, d) => s + d.value, 0);

  // Análisis para los paneles de mejor / menos eficiente. Requiere >= 2 centros con datos.
  const insights = useMemo(() => {
    const valid = aggregates.filter((a) => a.kilos > 0);
    if (valid.length < 2) return null;
    // dir 'higher' = mayor es mejor; 'lower' = menor es mejor.
    const pick = (key, dir) => {
      const s = [...valid].sort((a, b) => Number(a[key]) - Number(b[key]));
      const lo = s[0];
      const hi = s[s.length - 1];
      return dir === 'higher' ? { best: hi, worst: lo } : { best: lo, worst: hi };
    };
    const dominantCost = (a) => {
      const comps = [
        ['detergentes', a.costoProductosPorKg],
        ['servicios públicos', a.costoServiciosPorKg],
        ['mano de obra', a.costoManoObraPorKg],
      ];
      comps.sort((x, y) => y[1] - x[1]);
      return comps[0][0];
    };
    const total = valid.reduce((s, a) => s + a.kilos, 0);
    return { pick, dominantCost, total, fewCenters: false };
  }, [aggregates]);

  // Con un solo centro la torta de participación no aporta: se oculta.
  const singleCenter = centers.length === 1;

  // Composición del costo por kg mes a mes (solo aplica con un único centro).
  const composicionMensual = useMemo(() => {
    if (centers.length !== 1) return [];
    const c = centers[0];
    return periods.map((p) => {
      const o = kpisMap.get(`${c.id}|${p}`) || {};
      return {
        period: p,
        Productos: Number(o.costoProductosPorKg || 0),
        Servicios: Number(o.costoServiciosPorKg || 0),
        'Mano de obra': Number(o.costoManoObraPorKg || 0),
      };
    });
  }, [centers, periods, kpisMap]);

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="p-4 space-y-4">
      <Card>
        <CardHeader className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-b border-border/50">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} aria-label="Volver">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="bg-violet-500/15 p-2.5 rounded-lg">
              <BarChart3 className="h-7 w-7 text-violet-600 dark:text-violet-400" />
            </div>
            <div>
              <CardTitle className="text-2xl">OKR — Objetivos y Resultados Clave</CardTitle>
              <CardDescription className="mt-1">
                Selecciona los centros y el rango de meses; los resultados clave se muestran por mes y en relación a los kilos.
              </CardDescription>
            </div>
          </div>
        </CardHeader>

        <CardContent className="pt-6 space-y-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs font-medium text-muted-foreground">Desde</Label>
              <input type="month" value={from} onChange={(e) => setFrom(e.target.value)}
                className="h-10 rounded-lg border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-violet-500/20" />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs font-medium text-muted-foreground">Hasta</Label>
              <input type="month" value={to} onChange={(e) => setTo(e.target.value)}
                className="h-10 rounded-lg border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-violet-500/20" />
            </div>
            {loading && (
              <span className="h-10 flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
              </span>
            )}
          </div>

          {/* Selección de centros */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label className="text-xs font-medium text-muted-foreground">Centros a comparar</Label>
              <button type="button" onClick={toggleAll} className="text-xs text-violet-600 hover:underline">
                {selectedIds.length === allLaundries.length && allLaundries.length > 0 ? 'Quitar todos' : 'Seleccionar todos'}
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {allLaundries.map((l) => {
                const active = selectedIds.includes(l._id);
                return (
                  <button key={l._id} type="button" onClick={() => toggleId(l._id)}
                    className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${
                      active ? 'bg-violet-600 text-white border-violet-600' : 'bg-background text-muted-foreground border-border hover:border-violet-400'
                    }`}>
                    {l.nombre}
                  </button>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {!hasLoaded ? null : periods.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center">
            <p className="text-sm text-muted-foreground">
              No hay consolidados en este rango. Genéralos en{' '}
              <button onClick={() => navigate('/dashboard/cortes')} className="text-violet-600 hover:underline">
                Gestión de Cortes Mensuales
              </button>.
            </p>
          </CardContent>
        </Card>
      ) : centers.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center">
            <p className="text-sm text-muted-foreground">
              Selecciona uno o más centros arriba para construir el informe.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* 1. Kg procesados: tendencia + participación */}
          <Card>
            <CardHeader className="border-b border-border/50">
              <CardTitle className="text-lg">1. Kilogramos procesados</CardTitle>
              <CardDescription>Volumen por centro a lo largo del rango y participación de cada centro en el total.</CardDescription>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              <div className={`grid grid-cols-1 ${singleCenter ? '' : 'lg:grid-cols-2'} gap-6`}>
              <div>
                <div className="text-xs font-medium text-muted-foreground mb-2">Kg procesados por mes</div>
                <TrendBars data={buildTrend('kilosProcesados')} centers={centers} formatter={(v) => `${num0(v)} kg`} />
              </div>
              {!singleCenter && (
              <div>
                <div className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
                  <PieIcon className="h-3.5 w-3.5" /> Participación por centro ({num0(totalKilos)} kg totales)
                </div>
                <div style={{ width: '100%', height: 300 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                        {pieData.map((d, i) => <Cell key={d.name} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v) => `${num0(v)} kg`} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
              )}
              </div>
              {insights && (() => {
                const { best, worst } = insights.pick('kilos', 'higher');
                const t = insights.total || 1;
                return (
                  <InsightPanel
                    best={best}
                    worst={worst}
                    bestText={`Mayor volumen: ${num0(best.kilos)} kg (${((best.kilos / t) * 100).toFixed(0)}% del total)`}
                    worstText={`Menor volumen: ${num0(worst.kilos)} kg (${((worst.kilos / t) * 100).toFixed(0)}% del total)`}
                    suggestion={`${worst.nombre} aporta el menor volumen. Evaluar captación de clientes o redistribuir carga hacia centros con capacidad ociosa.`}
                  />
                );
              })()}
            </CardContent>
          </Card>

          {/* 2. % de rechazo */}
          <Card>
            <CardHeader className="border-b border-border/50">
              <CardTitle className="text-lg">2. % de Rechazo</CardTitle>
              <CardDescription>Kilos de rechazo sobre kilos procesados, por mes.</CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              <TrendBars data={buildTrend('porcentajeRechazo')} centers={centers} formatter={(v) => pct(v)} />
              {insights && (() => {
                const { best, worst } = insights.pick('pctRechazo', 'lower');
                return (
                  <InsightPanel
                    best={best}
                    worst={worst}
                    bestText={`Menor rechazo: ${pct(best.pctRechazo)}`}
                    worstText={`Mayor rechazo: ${pct(worst.pctRechazo)}`}
                    suggestion={`${worst.nombre} concentra el mayor % de rechazo. Revisar clasificación de ropa, dosificación química y control de calidad en recepción/lavado.`}
                  />
                );
              })()}
            </CardContent>
          </Card>

          {/* 3. Kg por hora autorizada */}
          <Card>
            <CardHeader className="border-b border-border/50">
              <CardTitle className="text-lg">3. Kilogramos por hora autorizada</CardTitle>
              <CardDescription>Productividad de la mano de obra: kg procesados por cada hora autorizada.</CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              <TrendBars data={buildTrend('kgPorHoraAutorizada')} centers={centers} formatter={(v) => `${dec2(v)} kg/h`} />
              {insights && (() => {
                const { best, worst } = insights.pick('kgPorHora', 'higher');
                return (
                  <InsightPanel
                    best={best}
                    worst={worst}
                    bestText={`Mayor productividad: ${dec2(best.kgPorHora)} kg/h`}
                    worstText={`Menor productividad: ${dec2(worst.kgPorHora)} kg/h`}
                    suggestion={`${worst.nombre} procesa menos kg por hora autorizada. Revisar programación de turnos y horas autorizadas frente a la carga real de trabajo.`}
                  />
                );
              })()}
            </CardContent>
          </Card>

          {/* 4. Costos por kilo (composición) */}
          <Card>
            <CardHeader className="border-b border-border/50">
              <CardTitle className="text-lg">4. Costo por kilo y su composición</CardTitle>
              <CardDescription>Costo por kg agregado del rango: detergentes + servicios + mano de obra (apilado = costo total/kg).</CardDescription>
            </CardHeader>
            <CardContent className="pt-4 space-y-6">
              <div>
                <div className="text-xs font-medium text-muted-foreground mb-2">Costo total por kg por mes</div>
                <TrendBars data={buildTrend('costoKiloTotal')} centers={centers} formatter={(v) => cop2(v)} />
              </div>
              <div>
              <div className="text-xs font-medium text-muted-foreground mb-2">
                {singleCenter ? 'Composición del costo por kg por mes' : 'Composición del costo por kg (agregado del rango)'}
              </div>
              <div style={{ width: '100%', height: 320 }}>
                <ResponsiveContainer>
                  <BarChart data={singleCenter ? composicionMensual : aggregates} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey={singleCenter ? 'period' : 'nombre'} tick={{ fontSize: 11 }} interval={0} angle={singleCenter ? 0 : -12} textAnchor={singleCenter ? 'middle' : 'end'} height={singleCenter ? 30 : 50} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(v) => cop2(v)} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="Productos" stackId="c" fill="#f59e0b">
                      <LabelList dataKey="Productos" position="center" fontSize={9} fill="#fff" formatter={hideZero(cop2)} />
                    </Bar>
                    <Bar dataKey="Servicios" stackId="c" fill="#3b82f6">
                      <LabelList dataKey="Servicios" position="center" fontSize={9} fill="#fff" formatter={hideZero(cop2)} />
                    </Bar>
                    <Bar dataKey="Mano de obra" stackId="c" fill="#10b981" radius={[4, 4, 0, 0]}>
                      <LabelList dataKey="Mano de obra" position="center" fontSize={9} fill="#fff" formatter={hideZero(cop2)} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              </div>
              {insights && (() => {
                const { best, worst } = insights.pick('costoKiloTotal', 'lower');
                const dom = insights.dominantCost(worst);
                return (
                  <InsightPanel
                    best={best}
                    worst={worst}
                    bestText={`Menor costo/kg: ${cop2(best.costoKiloTotal)}`}
                    worstText={`Mayor costo/kg: ${cop2(worst.costoKiloTotal)} — mayor componente: ${dom}`}
                    suggestion={`En ${worst.nombre} el costo por kilo está liderado por ${dom}. Revisar ${dom === 'detergentes' ? 'dosificación y precios de insumos' : dom === 'servicios públicos' ? 'consumos de agua/gas/energía y tarifas' : 'horas autorizadas vs. carga procesada'}.`}
                  />
                );
              })()}
            </CardContent>
          </Card>

          {/* 5. Producto por kilo */}
          <Card>
            <CardHeader className="border-b border-border/50">
              <CardTitle className="text-lg">5. Insumos / detergentes por kilo</CardTitle>
              <CardDescription>Litros de producto y costo de producto por kg (agregado del rango), y el producto más usado por centro.</CardDescription>
            </CardHeader>
            <CardContent className="pt-4 space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <div className="text-xs font-medium text-muted-foreground mb-2">Litros de producto por kg (por mes)</div>
                  <TrendBars data={buildTrend('litrosProductoPorKg')} centers={centers} formatter={(v) => `${dec2(v)} L/kg`} />
                </div>
                <div>
                  <div className="text-xs font-medium text-muted-foreground mb-2">Costo de producto por kg (por mes)</div>
                  <TrendBars data={buildTrend('costoProductosPorKg')} centers={centers} formatter={(v) => cop2(v)} />
                </div>
              </div>

              {insights && (() => {
                const { best, worst } = insights.pick('litrosPorKg', 'lower');
                return (
                  <InsightPanel
                    best={best}
                    worst={worst}
                    bestText={`Menor consumo: ${dec2(best.litrosPorKg)} L/kg (${cop2(best.costoProductosPorKg)}/kg)`}
                    worstText={`Mayor consumo: ${dec2(worst.litrosPorKg)} L/kg (${cop2(worst.costoProductosPorKg)}/kg)`}
                    suggestion={`${worst.nombre} usa más litros de producto por kilo. Revisar dosificación de detergentes, calibración de bombas dosificadoras y posibles fugas o sobreuso.`}
                  />
                );
              })()}

              <div>
                <div className="text-xs font-medium text-muted-foreground mb-2">Producto más usado por centro (último mes del rango)</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-muted-foreground border-b border-border">
                        <th className="py-2 pr-4 font-medium">Centro</th>
                        <th className="py-2 pr-4 font-medium">Producto</th>
                        <th className="py-2 pr-4 font-medium text-right">Cantidad usada</th>
                        <th className="py-2 pr-4 font-medium text-right">Costo</th>
                        <th className="py-2 pr-4 font-medium">Mes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {aggregates.map((a) => (
                        <tr key={a.id} className="border-b border-border/50 hover:bg-muted/30">
                          <td className="py-2 pr-4 font-medium">{a.nombre}</td>
                          <td className="py-2 pr-4">{a.productoTop?.nombre || <span className="text-muted-foreground">—</span>}</td>
                          <td className="py-2 pr-4 text-right">{a.productoTop ? dec2(a.productoTop.totalUsado) : '—'}</td>
                          <td className="py-2 pr-4 text-right">{a.productoTop ? cop(a.productoTop.totalCosto) : '—'}</td>
                          <td className="py-2 pr-4 text-muted-foreground">{a.topPeriod || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </motion.div>
  );
};

export default ComparativoLavanderiasPage;
